<template>
  <v-container fluid class="pa-6">
    <v-row>
      <v-col cols="12">
        <h1 class="text-h4 font-weight-bold mb-6">Настройки системы</h1>

        <!-- Loading state -->
        <v-skeleton-loader
          v-if="loading"
          type="card"
          class="mb-6"
        ></v-skeleton-loader>

        <!-- Settings form -->
        <v-card v-else elevation="2" rounded="lg">
          <v-card-title class="text-h6 font-weight-bold pa-6 pb-0">
            Основные настройки ({{ settings.name }})
          </v-card-title>

          <!-- Tabs -->
          <v-tabs v-model="activeTab" color="primary" class="px-6">
            <v-tab value="general">
              <v-icon start>mdi-cog</v-icon>
              Общие настройки
            </v-tab>
            <v-tab value="telegram">
              <v-icon start>mdi-telegram</v-icon>
              Мониторинг Telegram
            </v-tab>
            <v-tab value="dzen">
              <v-icon start>mdi-newspaper-variant-outline</v-icon>
              Мониторинг Дзен
            </v-tab>
            <v-tab value="x">
              <v-icon start>mdi-alpha-x-circle-outline</v-icon>
              Мониторинг X.com
            </v-tab>
            <v-tab value="matching">
              <v-icon start>mdi-engine</v-icon>
              Project Matching Engine
            </v-tab>
            <v-tab value="user_web">
              <v-icon start>mdi-web</v-icon>
              Мониторинг сайтов
            </v-tab>
            <v-tab value="search">
              <v-icon start>mdi-magnify</v-icon>
              Поисковики
            </v-tab>
            <v-tab value="tg_networks">
              <v-icon start>mdi-graph-outline</v-icon>
              Сетки каналов
            </v-tab>
            <v-tab value="tor">
              <v-icon start>mdi-incognito</v-icon>
              Tor
            </v-tab>
          </v-tabs>

          <v-divider></v-divider>

          <v-card-text class="pa-6">
            <v-form ref="form" v-model="valid" @submit.prevent="saveSettings">
              <v-tabs-window v-model="activeTab">
                <!-- General Settings Tab -->
                <v-tabs-window-item value="general">
                  <div class="settings-section">
                    <v-text-field
                      v-model.number="formData.frontend_refresh_rate"
                      label="Интервал автообновления (секунды)"
                      type="number"
                      min="5"
                      max="300"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(5), rules.maxValue(300)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-refresh"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Интервал автоматического обновления таблиц во фронтенде (5-300 секунд)
                    </p>

                    <!-- «Сообщить об ошибке»: the letters of users go to this address -->
                    <v-text-field
                      v-model="formData.feedback_email"
                      label="Почта для сообщений пользователей"
                      variant="outlined"
                      density="comfortable"
                      hide-details="auto"
                      prepend-inner-icon="mdi-email-outline"
                      placeholder="name@example.com"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Сюда приходят сообщения кнопки «Сообщить об ошибке», с системного почтового ящика. Пусто — кнопка ответит ошибкой.
                    </p>

                    <!-- The exit to hosts the server cannot reach: the bot, the alerts, LLM abroad -->
                    <v-select
                      v-model="formData.service_proxy_id"
                      :items="serviceProxyOptions"
                      item-title="text"
                      item-value="value"
                      label="Служебный прокси"
                      variant="outlined"
                      density="comfortable"
                      hide-details="auto"
                      prepend-inner-icon="mdi-shield-key-outline"
                      no-data-text="Нет HTTP-прокси с логином"
                      class="mb-2"
                    ></v-select>
                    <p class="text-caption text-medium-emphasis">
                      Через него идут бот платформы и оповещения (Telegram) и запросы к LLM за рубежом.
                      Только HTTP-прокси с логином. Смена действует в течение 5 минут; выбранный прокси
                      нельзя удалить, пока не выбран другой
                    </p>
                  </div>
                </v-tabs-window-item>

                <!-- Telegram Monitoring Tab -->
                <v-tabs-window-item value="telegram">
                  <div class="settings-section">
                    <v-switch
                      v-model="formData.is_telegram_monitoring"
                      :label="formData.is_telegram_monitoring ? 'Мониторинг включён' : 'Мониторинг выключен'"
                      color="primary"
                      inset
                      hide-details="auto"
                      class="mb-2"
                    >
                      <template v-slot:prepend>
                        <v-icon color="primary">mdi-radar</v-icon>
                      </template>
                    </v-switch>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Включить/выключить автоматический мониторинг Telegram каналов
                    </p>

                    <v-text-field
                      v-model.number="formData.telegram_monitoring_scheduler_interval"
                      label="Интервал планировщика (секунды)"
                      type="number"
                      min="1"
                      max="86400"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(1), rules.maxValue(86400)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-clock-outline"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Как часто запускать проверку Telegram каналов (1-86400 секунд)
                    </p>

                    <v-text-field
                      v-model.number="formData.telegram_monitoring_poll_window"
                      label="Окно опроса (часы)"
                      type="number"
                      min="0"
                      max="168"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.minValue(0), rules.maxValue(168)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-window-open"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Временное окно для сбора сообщений в часах (0 = без ограничений, максимум 168 часов)
                    </p>

                    <v-text-field
                      v-model.number="formData.telegram_channels_info_amount_per_tick"
                      label="Каналов за тик"
                      type="number"
                      min="1"
                      max="100"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(1), rules.maxValue(100)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-counter"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Количество каналов для обработки за один тик планировщика (1-100)
                    </p>

                    <v-text-field
                      v-model="formData.tg_from_date"
                      label="Начальная дата сбора сообщений"
                      type="date"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-calendar-start"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis">
                      Глубина сбора: новый канал при первом сборе листается назад до этой даты. Каналы, которые уже собираются, продолжают с последнего сообщения
                    </p>
                  </div>
                </v-tabs-window-item>

                <!-- Dzen Monitoring Tab -->
                <v-tabs-window-item value="dzen">
                  <div class="settings-section">
                    <!-- Section 1: News collection (homepage scraper) -->
                    <div class="text-subtitle-1 font-weight-bold mb-4">Сбор новостей (главная страница)</div>

                    <v-switch
                      v-model="formData.is_dzen_monitoring"
                      :label="formData.is_dzen_monitoring ? 'Сбор новостей включён' : 'Сбор новостей выключен'"
                      color="primary"
                      inset
                      hide-details="auto"
                      class="mb-2"
                    >
                      <template v-slot:prepend>
                        <v-icon color="primary">mdi-newspaper-variant-outline</v-icon>
                      </template>
                    </v-switch>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Автоматический сбор новостей с главной страницы Яндекс.Дзен (все рубрики)
                    </p>

                    <v-text-field
                      v-model.number="formData.dzen_monitoring_scheduler_interval"
                      label="Интервал сбора новостей (секунды)"
                      type="number"
                      min="60"
                      max="86400"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(60), rules.maxValue(86400)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-clock-outline"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-8">
                      Как часто запускать сбор новостей с главной страницы Дзен (60-86400 секунд)
                    </p>

                    <v-divider class="mb-6"></v-divider>

                    <!-- Section 2: Per-source channel monitoring -->
                    <div class="text-subtitle-1 font-weight-bold mb-4">Мониторинг каналов (источники)</div>

                    <v-switch
                      v-model="formData.is_dzen_channel_monitoring"
                      :label="formData.is_dzen_channel_monitoring ? 'Мониторинг каналов включён' : 'Мониторинг каналов выключен'"
                      color="primary"
                      inset
                      hide-details="auto"
                      class="mb-2"
                    >
                      <template v-slot:prepend>
                        <v-icon color="primary">mdi-radar</v-icon>
                      </template>
                    </v-switch>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Глобальный переключатель мониторинга каналов Дзен. При выключении все каналы перестают проверяться,
                      даже если у них включён мониторинг индивидуально.
                    </p>

                    <v-text-field
                      v-model.number="formData.dzen_monitoring_poll_window"
                      label="Окно опроса каналов (часы)"
                      type="number"
                      min="1"
                      max="168"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(1), rules.maxValue(168)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-window-open"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Через сколько часов повторно проверять каждый канал (1-168 часов, по умолчанию 12)
                    </p>

                    <v-text-field
                      v-model="formData.dzen_from_date"
                      label="Начальная дата сбора статей"
                      type="date"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-calendar-start"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis">
                      Дата, до которой собираются статьи при мониторинге каналов (формат ГГГГ-ММ-ДД)
                    </p>
                  </div>
                </v-tabs-window-item>

                <!-- X.com Monitoring Tab -->
                <v-tabs-window-item value="x">
                  <div class="settings-section">
                    <v-switch
                      v-model="formData.is_x_monitoring"
                      :label="formData.is_x_monitoring ? 'Мониторинг включён' : 'Мониторинг выключен'"
                      color="primary"
                      inset
                      hide-details="auto"
                      class="mb-2"
                    >
                      <template v-slot:prepend>
                        <v-icon color="primary">mdi-radar</v-icon>
                      </template>
                    </v-switch>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Включить/выключить автоматический мониторинг X.com (Twitter)
                    </p>

                    <v-text-field
                      v-model="formData.x_from_date"
                      label="Начальная дата сбора твитов"
                      type="date"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-calendar-start"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Дата, до которой собираются твиты при мониторинге аккаунтов и поисковых запросов (формат ГГГГ-ММ-ДД)
                    </p>

                    <v-text-field
                      v-model.number="formData.x_timeline_max_tweets"
                      label="Максимум твитов в ленте аккаунта"
                      type="number"
                      min="10"
                      max="1000"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(10), rules.maxValue(1000)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-account-details"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Сколько твитов вытягивать за один прогон мониторинга аккаунта (10-1000). Парсер остановится раньше, если встретит уже известные твиты.
                    </p>

                    <v-text-field
                      v-model.number="formData.x_search_max_tweets"
                      label="Максимум твитов при поиске"
                      type="number"
                      min="10"
                      max="1000"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(10), rules.maxValue(1000)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-magnify"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis">
                      Сколько твитов вытягивать за один прогон поиска (10-1000). Парсер остановится раньше, если встретит уже известные твиты.
                    </p>
                  </div>
                </v-tabs-window-item>

                <!-- Project Matching Engine Tab -->
                <v-tabs-window-item value="matching">
                  <div class="settings-section">
                    <v-switch
                      v-model="formData.is_project_matching_enabled"
                      :label="formData.is_project_matching_enabled ? 'Matching Engine включён' : 'Matching Engine выключен'"
                      color="primary"
                      inset
                      hide-details="auto"
                      class="mb-2"
                    >
                      <template v-slot:prepend>
                        <v-icon color="primary">mdi-engine</v-icon>
                      </template>
                    </v-switch>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Включить/выключить автоматическое сопоставление проектов с контентом
                    </p>

                    <v-text-field
                      v-model.number="formData.matching_dispatcher_interval_minutes"
                      label="Интервал dispatcher (минуты)"
                      type="number"
                      min="1"
                      max="1440"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(1), rules.maxValue(1440)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-clock-outline"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Как часто запускать dispatcher для обработки проектов (1-1440 минут)
                    </p>

                    <v-text-field
                      v-model.number="formData.matching_projects_batch_size"
                      label="Проектов за запуск"
                      type="number"
                      min="1"
                      max="100"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(1), rules.maxValue(100)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-package-variant"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Количество проектов для обработки за один запуск (1-100)
                    </p>

                    <v-text-field
                      v-model.number="formData.matching_lock_duration_minutes"
                      label="Длительность блокировки (минуты)"
                      type="number"
                      min="5"
                      max="120"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(5), rules.maxValue(120)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-lock-clock"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      На сколько минут блокировать проект при обработке (5-120 минут)
                    </p>

                    <v-text-field
                      v-model.number="formData.matching_content_limit"
                      label="Лимит сообщений первой проверки"
                      type="number"
                      min="100"
                      max="10000"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(100), rules.maxValue(10000)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-database"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Новый проект при первой проверке получает не больше стольких самых свежих сообщений (100-10000); регулярные проверки не ограничены
                    </p>

                    <v-text-field
                      v-model.number="formData.matching_default_lookback_days"
                      label="Период поиска назад (дни)"
                      type="number"
                      min="1"
                      max="365"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(1), rules.maxValue(168)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-timer-refresh-outline"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Первая проверка нового проекта берёт сообщения, опубликованные за столько дней (1-365)
                    </p>

                    <v-text-field
                      v-model.number="formData.story_max_age_days"
                      label="Возраст сюжета (дни)"
                      type="number"
                      min="0"
                      max="30"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(0), rules.maxValue(30)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-calendar-range"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis">
                      Сообщение попадает только в сюжет, начавшийся не дальше стольких дней от его публикации: дайджесты и сводки разных дней не сливаются в один сюжет (0 — без предела, 0-30)
                    </p>
                  </div>
                </v-tabs-window-item>

                <!-- User Web Monitoring Tab -->
                <v-tabs-window-item value="user_web">
                  <div class="settings-section">
                    <v-text-field
                      v-model.number="formData.user_web_simple_check_minutes"
                      label="Частота проверки простых сайтов (минуты)"
                      type="number"
                      min="5"
                      max="10080"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(5), rules.maxValue(10080)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-web"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Как часто опрашивать сайты, которые удаётся читать через datacenter-прокси (5-10080 минут, по умолчанию 60)
                    </p>

                    <v-text-field
                      v-model.number="formData.user_web_complex_check_minutes"
                      label="Частота проверки сложных сайтов (минуты)"
                      type="number"
                      min="5"
                      max="10080"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(5), rules.maxValue(10080)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-shield-lock-outline"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis">
                      Как часто опрашивать сайты, для которых нужен residential-прокси / FlareSolverr (5-10080 минут, по умолчанию 1440 = раз в сутки). Тип прокси для каждого сайта определяется автоматически после первой успешной выкачки.
                    </p>
                  </div>
                </v-tabs-window-item>

                <!-- Search Engines Tab -->
                <v-tabs-window-item value="search">
                  <div class="settings-section">
                    <p class="text-caption text-medium-emphasis mb-6">
                      Сбор из поисковых систем (Google через Serpent API, Яндекс через Yandex Search API). Значения хранятся в БД;
                      если поле пустое, воркер использует дефолт из кода.
                    </p>

                    <v-text-field
                      v-model.number="formData.search_serps_frequency_hours"
                      label="Частота пере-сбора запроса (часы)"
                      type="number"
                      min="1"
                      max="720"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(1), rules.maxValue(720)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-clock-outline"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Как часто повторно опрашивать поисковик по каждому запросу (1-720 часов, по умолчанию 12)
                    </p>

                    <v-text-field
                      v-model.number="formData.search_default_depth"
                      label="Результатов на запрос по умолчанию"
                      type="number"
                      min="1"
                      max="1000"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(1), rules.maxValue(1000)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-format-list-numbered"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Глубина выдачи по умолчанию, если у источника она не задана (1-1000, по умолчанию 10)
                    </p>

                    <v-text-field
                      v-model.number="formData.search_crawl_concurrency"
                      label="Параллельных крауллов"
                      type="number"
                      min="1"
                      max="50"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(1), rules.maxValue(50)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-download-multiple"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Лимит одновременных выкачек страниц (задаётся через max_usage прокси; остальные ждут свободный прокси). 1-50, по умолчанию 5
                    </p>

                    <v-text-field
                      v-model.number="formData.search_crawl_wait_seconds"
                      label="Пауза между попытками, если нет прокси (сек)"
                      type="number"
                      min="5"
                      max="600"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(5), rules.maxValue(600)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-timer-sand"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Через сколько секунд перепроверять наличие свободного прокси (5-600, по умолчанию 15)
                    </p>

                    <v-text-field
                      v-model.number="formData.search_crawl_max_wait_seconds"
                      label="Максимум ожидания прокси перед сниппетом (сек)"
                      type="number"
                      min="60"
                      max="86400"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(60), rules.maxValue(86400)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-timer-alert-outline"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Сколько всего ждать свободный прокси, прежде чем сохранить сниппет вместо полного текста (60-86400 сек, по умолчанию 3600 = 1 час)
                    </p>

                    <v-text-field
                      v-model.number="formData.search_site_max_attempts"
                      label="Ретраев, если прокси есть, но сайт не отдал"
                      type="number"
                      min="0"
                      max="10"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(0), rules.maxValue(10)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-refresh"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Сколько раз повторять выкачку, когда прокси доступен, но страница не отдала контент (0-10, по умолчанию 3)
                    </p>

                    <v-switch
                      v-model="formData.search_nodriver_enabled"
                      :label="formData.search_nodriver_enabled ? 'Nodriver включён' : 'Nodriver выключен'"
                      color="primary"
                      inset
                      hide-details="auto"
                      class="mb-2"
                    >
                      <template v-slot:prepend>
                        <v-icon color="primary">mdi-robot-outline</v-icon>
                      </template>
                    </v-switch>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Использовать 4-й слой извлечения (Nodriver — stealth Chromium) для защищённых сайтов, когда curl_cffi / Playwright / FlareSolverr не справились
                    </p>

                    <div class="text-subtitle-2 font-weight-bold mb-3">Отслеживание изменений</div>
                    <v-text-field
                      v-model.number="formData.search_recrawl_check_every_hours"
                      label="Проверять каждые (часы)"
                      type="number" min="1" max="168"
                      variant="outlined" density="comfortable"
                      :rules="[rules.required, rules.minValue(1), rules.maxValue(168)]"
                      hide-details="auto" prepend-inner-icon="mdi-timer-refresh-outline"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Как часто проверять найденные сообщения на изменения внутри окна наблюдения проекта (1-168 ч, по умолчанию 6).
                    </p>

                    <v-text-field
                      v-model.number="formData.search_recrawl_max_urls_per_cycle"
                      label="Максимум URL на проект за прогон"
                      type="number" min="1" max="5000"
                      variant="outlined" density="comfortable"
                      :rules="[rules.required, rules.minValue(1), rules.maxValue(5000)]"
                      hide-details="auto" prepend-inner-icon="mdi-counter"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis">
                      Кап на число перекачиваемых URL одного проекта за один прогон (1-5000, по умолчанию 200) — защита от бурста.
                    </p>
                  </div>
                </v-tabs-window-item>
                <!-- Telegram channel networks Tab -->
                <!-- Tor: стоп-слова для страниц .onion -->
                <v-tabs-window-item value="tor">
                  <div class="settings-section">
                    <p class="text-body-2 text-medium-emphasis mb-6">
                      Стоп-слова для страниц из сети Tor (сайты .onion и результаты onion-поисковиков). Проверяются и в оригинале
                      страницы, и в её переводе. При совпадении страница не попадает в индекс, уже сохранённый документ удаляется
                      вместе с найденными по нему сообщениями проектов, а адрес заносится в стоп-список и больше не скачивается. Регистр не важен.
                    </p>
                    <v-textarea
                      v-model="formData.onion_block_words"
                      label="Целые слова, по одному на строку"
                      hint="Срабатывает только на слово целиком: «оружие» найдёт «Оружие», но не «вооружение»"
                      persistent-hint
                      variant="outlined"
                      rows="8"
                      auto-grow
                      class="mb-6"
                    ></v-textarea>
                    <v-textarea
                      v-model="formData.onion_block_substrings"
                      label="Вхождения, по одному на строку"
                      hint="Срабатывает на часть слова: «наркот» найдёт «наркотики» и «наркоторговля»"
                      persistent-hint
                      variant="outlined"
                      rows="8"
                      auto-grow
                    ></v-textarea>
                  </div>
                </v-tabs-window-item>

                <v-tabs-window-item value="tg_networks">
                  <div class="settings-section">
                    <p class="text-body-2 text-medium-emphasis mb-6">
                      Кластеризация графа связей Telegram-каналов (репосты, ссылки, одинаковые посты) в «сетки». Пересчёт — раз в час фоновой задачей; изменения применяются со следующего прогона.
                    </p>
                    <v-text-field
                      v-model.number="formData.tg_network_weight_forward"
                      label="Вес ребра «репост»"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(0), rules.maxValue(100)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-share-outline"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Сколько «стоит» один репост при построении графа для кластеризации (0-100, по умолчанию 3)
                    </p>
                    <v-text-field
                      v-model.number="formData.tg_network_weight_link"
                      label="Вес ребра «ссылка»"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(0), rules.maxValue(100)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-link-variant"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Вес ссылки t.me/канал в тексте поста (0-100, по умолчанию 1)
                    </p>
                    <v-text-field
                      v-model.number="formData.tg_network_weight_coposting"
                      label="Вес ребра «одинаковые посты»"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(0), rules.maxValue(100)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-content-copy"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Вес координированного постинга — одинаковый текст в нескольких каналах за короткое окно (0-100, по умолчанию 2)
                    </p>
                    <v-text-field
                      v-model.number="formData.tg_network_weight_reply"
                      label="Вес ребра «ответ»"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(0), rules.maxValue(100)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-reply-outline"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Вес ответа на пост другого канала; 0 — не учитывать (по умолчанию 0)
                    </p>
                    <v-text-field
                      v-model.number="formData.tg_network_min_weight"
                      label="Минимум событий у ребра"
                      type="number"
                      min="1"
                      max="1000"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(1), rules.maxValue(1000)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-filter-outline"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Ребро участвует в кластеризации, только если событий (постов) не меньше этого числа (1-1000, по умолчанию 2)
                    </p>
                    <v-text-field
                      v-model.number="formData.tg_network_edge_window_days"
                      label="Окно живых рёбер (дни)"
                      type="number"
                      min="1"
                      max="3650"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(1), rules.maxValue(3650)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-calendar-range"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Учитывать только связи, замеченные за последние N дней (1-3650, по умолчанию 180)
                    </p>
                    <v-text-field
                      v-model.number="formData.tg_network_resolution"
                      label="Разрешение Louvain"
                      type="number"
                      min="0.1"
                      max="20"
                      step="0.1"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(0.1), rules.maxValue(20)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-tune-variant"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Больше единицы — мельче сети и меньше слияний, меньше единицы — крупнее. Замер: при 1.0 небольшие сети «прилипали» к крупным, при 1.5–3 держатся отдельно (по умолчанию 3)
                    </p>
                    <v-text-field
                      v-model.number="formData.tg_network_min_size"
                      label="Минимальный размер сетки"
                      type="number"
                      min="2"
                      max="1000"
                      variant="outlined"
                      density="comfortable"
                      :rules="[rules.required, rules.minValue(2), rules.maxValue(1000)]"
                      hide-details="auto"
                      prepend-inner-icon="mdi-account-group-outline"
                      class="mb-2"
                    ></v-text-field>
                    <p class="text-caption text-medium-emphasis mb-6">
                      Кластеры меньше этого размера не сохраняются как сетки (2-1000, по умолчанию 3)
                    </p>
                  </div>
                </v-tabs-window-item>
              </v-tabs-window>
            </v-form>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-6">
            <v-btn
              variant="outlined"
              color="secondary"
              @click="resetForm"
              :disabled="saving"
            >
              Отменить
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              size="large"
              :loading="saving"
              @click="saveSettings"
              :disabled="!valid"
              class="save-button"
              prepend-icon="mdi-content-save"
            >
              Сохранить настройки
            </v-btn>
          </v-card-actions>
        </v-card>

        <!-- Status information -->
        <v-card v-if="!loading" elevation="1" rounded="lg" class="mt-6">
          <v-card-title class="text-h6 font-weight-bold pa-6">
            Информация о настройках
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text class="pa-6">
            <v-list density="comfortable">
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="info">mdi-identifier</v-icon>
                </template>
                <v-list-item-title>ID настройки: {{ settings.id }}</v-list-item-title>
              </v-list-item>

              <v-list-item>
                <template v-slot:prepend>
                  <v-icon color="info">mdi-tag</v-icon>
                </template>
                <v-list-item-title>Название: {{ settings.name }}</v-list-item-title>
              </v-list-item>

              <v-list-item>
                <template v-slot:prepend>
                  <v-icon :color="formData.is_telegram_monitoring ? 'success' : 'warning'">
                    {{ formData.is_telegram_monitoring ? 'mdi-check-circle' : 'mdi-pause-circle' }}
                  </v-icon>
                </template>
                <v-list-item-title>
                  Мониторинг Telegram: {{ formData.is_telegram_monitoring ? 'Активен' : 'Приостановлен' }}
                </v-list-item-title>
              </v-list-item>

              <v-list-item>
                <template v-slot:prepend>
                  <v-icon :color="formData.is_dzen_monitoring ? 'success' : 'warning'">
                    {{ formData.is_dzen_monitoring ? 'mdi-check-circle' : 'mdi-pause-circle' }}
                  </v-icon>
                </template>
                <v-list-item-title>
                  Сбор новостей Дзен: {{ formData.is_dzen_monitoring ? 'Активен' : 'Приостановлен' }}
                </v-list-item-title>
              </v-list-item>

              <v-list-item>
                <template v-slot:prepend>
                  <v-icon :color="formData.is_project_matching_enabled ? 'success' : 'warning'">
                    {{ formData.is_project_matching_enabled ? 'mdi-check-circle' : 'mdi-pause-circle' }}
                  </v-icon>
                </template>
                <v-list-item-title>
                  Project Matching Engine: {{ formData.is_project_matching_enabled ? 'Активен' : 'Приостановлен' }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import apiClient from '@/apiClient'

export default {
  name: 'Settings',

  data() {
    return {
      loading: true,
      saving: false,
      valid: false,
      activeTab: 'general',
      settings: {
        id: null,
        name: 'base',
        is_telegram_monitoring: false,
        telegram_monitoring_scheduler_interval: 60,
        telegram_monitoring_poll_window: 0,
        telegram_channels_info_amount_per_tick: 10,
        is_dzen_monitoring: false,
        dzen_monitoring_scheduler_interval: 3600,
        is_dzen_channel_monitoring: false,
        dzen_monitoring_poll_window: 12,
        dzen_from_date: '2025-01-01',
        is_x_monitoring: true,
        x_from_date: '2025-01-01',
        tg_from_date: '2025-01-01',
        service_proxy_id: null,
        feedback_email: '',
        x_search_max_tweets: 100,
        x_timeline_max_tweets: 100,
        is_project_matching_enabled: false,
        matching_dispatcher_interval_minutes: 5,
        matching_projects_batch_size: 10,
        matching_lock_duration_minutes: 15,
        matching_content_limit: 5000,
        matching_default_lookback_days: 90,
        story_max_age_days: 3,
        user_web_simple_check_minutes: 60,
        user_web_complex_check_minutes: 1440,
        search_serps_frequency_hours: 12,
        search_crawl_concurrency: 5,
        search_crawl_wait_seconds: 15,
        search_crawl_max_wait_seconds: 3600,
        search_site_max_attempts: 3,
        search_default_depth: 10,
        search_nodriver_enabled: true,
        onion_block_words: '',
        onion_block_substrings: '',
        search_recrawl_check_every_hours: 6,
        search_recrawl_max_urls_per_cycle: 200,
        tg_network_weight_forward: 3,
        tg_network_weight_link: 0.5,
        tg_network_weight_coposting: 2,
        tg_network_weight_reply: 0,
        tg_network_min_weight: 2,
        tg_network_edge_window_days: 180,
        tg_network_min_size: 3,
        tg_network_resolution: 3,
        frontend_refresh_rate: 30
      },
      formData: {
        is_telegram_monitoring: false,
        telegram_monitoring_scheduler_interval: 60,
        telegram_monitoring_poll_window: 0,
        telegram_channels_info_amount_per_tick: 10,
        is_dzen_monitoring: false,
        dzen_monitoring_scheduler_interval: 3600,
        is_dzen_channel_monitoring: false,
        dzen_monitoring_poll_window: 12,
        dzen_from_date: '2025-01-01',
        is_x_monitoring: true,
        x_from_date: '2025-01-01',
        tg_from_date: '2025-01-01',
        service_proxy_id: null,
        feedback_email: '',
        x_search_max_tweets: 100,
        x_timeline_max_tweets: 100,
        is_project_matching_enabled: false,
        matching_dispatcher_interval_minutes: 5,
        matching_projects_batch_size: 10,
        matching_lock_duration_minutes: 15,
        matching_content_limit: 5000,
        matching_default_lookback_days: 90,
        story_max_age_days: 3,
        user_web_simple_check_minutes: 60,
        user_web_complex_check_minutes: 1440,
        search_serps_frequency_hours: 12,
        search_crawl_concurrency: 5,
        search_crawl_wait_seconds: 15,
        search_crawl_max_wait_seconds: 3600,
        search_site_max_attempts: 3,
        search_default_depth: 10,
        search_nodriver_enabled: true,
        onion_block_words: '',
        onion_block_substrings: '',
        search_recrawl_check_every_hours: 6,
        search_recrawl_max_urls_per_cycle: 200,
        tg_network_weight_forward: 3,
        tg_network_weight_link: 0.5,
        tg_network_weight_coposting: 2,
        tg_network_weight_reply: 0,
        tg_network_min_weight: 2,
        tg_network_edge_window_days: 180,
        tg_network_min_size: 3,
        tg_network_resolution: 3,
        frontend_refresh_rate: 30
      },
      serviceProxyOptions: [],
      rules: {
        required: value => !!value || value === 0 || 'Поле обязательно для заполнения',
        minValue: (min) => value => value >= min || `Значение должно быть не менее ${min}`,
        maxValue: (max) => value => value <= max || `Значение должно быть не более ${max}`
      }
    }
  },

  async mounted() {
    this.loadServiceProxies()
    await this.loadSettings()
  },

  methods: {
    // HTTP proxies with a login: only they fit the egress gateway (core checks it as well)
    async loadServiceProxies() {
      try {
        const resp = await apiClient.get('/admin/proxies')
        this.serviceProxyOptions = (resp.data.items || [])
          .filter(p => String(p.proxy_type).toUpperCase() === 'HTTP' && p.has_auth)
          .map(p => ({
            value: p.id,
            text: [`#${p.id} ${p.ip_port}`, p.provider, p.country_code, p.status].filter(Boolean).join(' · '),
          }))
      } catch (e) {
        this.serviceProxyOptions = []
      }
    },

    async loadSettings() {
      try {
        this.loading = true
        const response = await apiClient.get('/admin/settings', {
          params: { name: 'base' }
        })

        this.settings = response.data
        this.formData = {
          is_telegram_monitoring: response.data.is_telegram_monitoring,
          telegram_monitoring_scheduler_interval: response.data.telegram_monitoring_scheduler_interval,
          telegram_monitoring_poll_window: response.data.telegram_monitoring_poll_window,
          telegram_channels_info_amount_per_tick: response.data.telegram_channels_info_amount_per_tick,
          is_dzen_monitoring: response.data.is_dzen_monitoring,
          dzen_monitoring_scheduler_interval: response.data.dzen_monitoring_scheduler_interval,
          is_dzen_channel_monitoring: response.data.is_dzen_channel_monitoring || false,
          dzen_monitoring_poll_window: response.data.dzen_monitoring_poll_window || 12,
          dzen_from_date: response.data.dzen_from_date || '2025-01-01',
          is_x_monitoring: response.data.is_x_monitoring ?? true,
          x_from_date: response.data.x_from_date || '2025-01-01',
          tg_from_date: response.data.tg_from_date || '2025-01-01',
          service_proxy_id: response.data.service_proxy_id ?? null,
          feedback_email: response.data.feedback_email || '',
          x_search_max_tweets: response.data.x_search_max_tweets || 100,
          x_timeline_max_tweets: response.data.x_timeline_max_tweets || 100,
          is_project_matching_enabled: response.data.is_project_matching_enabled,
          matching_dispatcher_interval_minutes: response.data.matching_dispatcher_interval_minutes,
          matching_projects_batch_size: response.data.matching_projects_batch_size,
          matching_lock_duration_minutes: response.data.matching_lock_duration_minutes,
          matching_content_limit: response.data.matching_content_limit,
          matching_default_lookback_days: response.data.matching_default_lookback_days,
          story_max_age_days: response.data.story_max_age_days ?? 3,
          user_web_simple_check_minutes: response.data.user_web_simple_check_minutes ?? 60,
          user_web_complex_check_minutes: response.data.user_web_complex_check_minutes ?? 1440,
          search_serps_frequency_hours: response.data.search_serps_frequency_hours ?? 12,
          search_crawl_concurrency: response.data.search_crawl_concurrency ?? 5,
          search_crawl_wait_seconds: response.data.search_crawl_wait_seconds ?? 15,
          search_crawl_max_wait_seconds: response.data.search_crawl_max_wait_seconds ?? 3600,
          search_site_max_attempts: response.data.search_site_max_attempts ?? 3,
          search_default_depth: response.data.search_default_depth ?? 10,
          search_nodriver_enabled: response.data.search_nodriver_enabled ?? true,
          onion_block_words: response.data.onion_block_words || '',
          onion_block_substrings: response.data.onion_block_substrings || '',
          search_recrawl_check_every_hours: response.data.search_recrawl_check_every_hours ?? 6,
          search_recrawl_max_urls_per_cycle: response.data.search_recrawl_max_urls_per_cycle ?? 200,
          frontend_refresh_rate: response.data.frontend_refresh_rate
        }

      } catch (error) {
        console.error('Error loading settings:', error)
        this.$emit('show-message', 'Ошибка при загрузке настроек', true)
      } finally {
        this.loading = false
      }
    },

    async saveSettings() {
      if (!this.valid) return

      try {
        this.saving = true

        const response = await apiClient.put('/admin/settings', this.formData, {
          params: { name: 'base' }
        })

        this.settings = response.data
        this.$emit('show-message', 'Настройки успешно сохранены', false)

      } catch (error) {
        console.error('Error saving settings:', error)
        let errorMessage = 'Ошибка при сохранении настроек'

        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail
        }

        this.$emit('show-message', errorMessage, true)
      } finally {
        this.saving = false
      }
    },

    resetForm() {
      this.formData = {
        is_telegram_monitoring: this.settings.is_telegram_monitoring,
        telegram_monitoring_scheduler_interval: this.settings.telegram_monitoring_scheduler_interval,
        telegram_monitoring_poll_window: this.settings.telegram_monitoring_poll_window,
        telegram_channels_info_amount_per_tick: this.settings.telegram_channels_info_amount_per_tick,
        is_dzen_monitoring: this.settings.is_dzen_monitoring,
        dzen_monitoring_scheduler_interval: this.settings.dzen_monitoring_scheduler_interval,
        is_dzen_channel_monitoring: this.settings.is_dzen_channel_monitoring || false,
        dzen_monitoring_poll_window: this.settings.dzen_monitoring_poll_window || 12,
        dzen_from_date: this.settings.dzen_from_date || '2025-01-01',
        is_x_monitoring: this.settings.is_x_monitoring ?? true,
        x_from_date: this.settings.x_from_date || '2025-01-01',
        tg_from_date: this.settings.tg_from_date || '2025-01-01',
        service_proxy_id: this.settings.service_proxy_id ?? null,
        x_search_max_tweets: this.settings.x_search_max_tweets || 100,
        x_timeline_max_tweets: this.settings.x_timeline_max_tweets || 100,
        is_project_matching_enabled: this.settings.is_project_matching_enabled,
        matching_dispatcher_interval_minutes: this.settings.matching_dispatcher_interval_minutes,
        matching_projects_batch_size: this.settings.matching_projects_batch_size,
        matching_lock_duration_minutes: this.settings.matching_lock_duration_minutes,
        matching_content_limit: this.settings.matching_content_limit,
        matching_default_lookback_days: this.settings.matching_default_lookback_days,
        story_max_age_days: this.settings.story_max_age_days ?? 3,
        user_web_simple_check_minutes: this.settings.user_web_simple_check_minutes ?? 60,
        user_web_complex_check_minutes: this.settings.user_web_complex_check_minutes ?? 1440,
        search_serps_frequency_hours: this.settings.search_serps_frequency_hours ?? 12,
        search_crawl_concurrency: this.settings.search_crawl_concurrency ?? 5,
        search_crawl_wait_seconds: this.settings.search_crawl_wait_seconds ?? 15,
        search_crawl_max_wait_seconds: this.settings.search_crawl_max_wait_seconds ?? 3600,
        search_site_max_attempts: this.settings.search_site_max_attempts ?? 3,
        search_default_depth: this.settings.search_default_depth ?? 10,
        search_nodriver_enabled: this.settings.search_nodriver_enabled ?? true,
        search_recrawl_check_every_hours: this.settings.search_recrawl_check_every_hours ?? 6,
        search_recrawl_max_urls_per_cycle: this.settings.search_recrawl_max_urls_per_cycle ?? 200,
        frontend_refresh_rate: this.settings.frontend_refresh_rate
      }

      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    }
  }
}
</script>

<style scoped>
/* Custom styling for settings form */
.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
}

/* Settings section layout */
.settings-section {
  max-width: 600px;
  padding-top: 16px;
}

/* Save button styling */
.save-button {
  min-width: 200px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

/* Status information styling */
.v-list-item {
  border-radius: 8px;
  margin-bottom: 4px;
}

.v-list-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.04);
}

/* Responsive design */
@media (max-width: 960px) {
  .v-container {
    padding: 16px !important;
  }

  .v-card-text {
    padding: 20px !important;
  }

  .v-card-actions {
    padding: 20px !important;
    flex-direction: column;
    gap: 12px;
  }

  .v-card-actions .v-btn {
    width: 100%;
  }

  .settings-section {
    max-width: 100%;
  }
}
</style>
